/*
 * Collection card: image carousel + variant swatches
 * Added Oct 2026. Loaded from snippets/card-product.liquid.
 *
 * - <card-carousel>: scroll-snap track with hover arrows (desktop) and swipe (touch), dots.
 * - .card-swatch buttons: swap the card image/price/links/quick-add variant without leaving the page.
 *   Uses document-level delegation so it keeps working after facets.js re-renders the grid.
 */
(function () {
  'use strict';

  // The script tag is rendered once per card; only initialise once.
  if (window.__mueCardCarouselInit) return;
  window.__mueCardCarouselInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Carousel ---------------- */
  if (!customElements.get('card-carousel')) {
    customElements.define(
      'card-carousel',
      class CardCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.card-carousel__track');
          this.allSlides = Array.prototype.slice.call(this.querySelectorAll('.card-carousel__slide'));
          this.dotsWrap = this.querySelector('.card-carousel__dots');
          this.prevBtn = this.querySelector('.card-carousel__arrow--prev');
          this.nextBtn = this.querySelector('.card-carousel__arrow--next');
          this.index = 0;
          this.slides = [];
          this.dots = [];
          if (!this.track || this.allSlides.length < 1) return;

          this.setGroup(this.dataset.activeGroup || '0', true);

          this.onScroll = this.onScroll.bind(this);
          this.track.addEventListener('scroll', this.onScroll, { passive: true });

          if (this.prevBtn) this.prevBtn.addEventListener('click', this.onArrow.bind(this, -1));
          if (this.nextBtn) this.nextBtn.addEventListener('click', this.onArrow.bind(this, 1));

          // Stop a drag on an arrow from being treated as a card click / link drag.
          [this.prevBtn, this.nextBtn].forEach(function (btn) {
            if (!btn) return;
            btn.addEventListener('mousedown', function (e) { e.preventDefault(); });
          });
        }

        disconnectedCallback() {
          if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        }

        onArrow(direction, event) {
          event.preventDefault();
          event.stopPropagation();
          var next = this.index + direction;
          if (next < 0) next = this.slides.length - 1;
          if (next > this.slides.length - 1) next = 0;
          this.goTo(next);
        }

        onScroll() {
          if (this.scrollRaf) return;
          var self = this;
          this.scrollRaf = window.requestAnimationFrame(function () {
            self.scrollRaf = null;
            var w = self.track.clientWidth || 1;
            var i = Math.round(self.track.scrollLeft / w);
            self.setActive(Math.max(0, Math.min(i, self.slides.length - 1)));
          });
        }

        goTo(index) {
          if (!this.track || !this.slides[index]) return;
          // Hidden slides take no space, so position = index * slide width.
          var left = index * this.track.clientWidth;
          if (typeof this.track.scrollTo === 'function') {
            // Smooth scrolling does not run in background tabs, so jump instantly there.
            var smooth = !reduceMotion && !document.hidden;
            this.track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
          } else {
            this.track.scrollLeft = left;
          }
          this.setActive(index);
        }

        /*
         * Show only the slides for one variant group (plus group "0" = images that belong to
         * every variant), rebuild the dots, and jump back to the first slide.
         */
        setGroup(groupId, initial) {
          groupId = String(groupId);
          var hasGroup = this.allSlides.some(function (s) { return s.dataset.group === groupId; });
          if (!hasGroup) groupId = null; // unknown group: show everything
          this.slides = this.allSlides.filter(function (s) {
            var show = groupId === null || s.dataset.group === '0' || s.dataset.group === groupId;
            s.classList.toggle('card-carousel__slide--hidden', !show);
            return show;
          });
          this.renderDots();
          var multiple = this.slides.length > 1;
          if (this.prevBtn) this.prevBtn.hidden = !multiple;
          if (this.nextBtn) this.nextBtn.hidden = !multiple;
          if (!initial && this.track) this.track.scrollLeft = 0;
          this.setActive(0);
          return groupId !== null;
        }

        renderDots() {
          if (!this.dotsWrap) return;
          this.dotsWrap.innerHTML = '';
          this.dots = [];
          if (this.slides.length < 2) return;
          for (var i = 0; i < this.slides.length; i++) {
            var dot = document.createElement('span');
            dot.className = 'card-carousel__dot' + (i === 0 ? ' is-active' : '');
            this.dotsWrap.appendChild(dot);
            this.dots.push(dot);
          }
        }

        goToMedia(mediaId) {
          if (!mediaId) return false;
          for (var i = 0; i < this.slides.length; i++) {
            if (this.slides[i].dataset.mediaId === String(mediaId)) {
              this.goTo(i);
              return true;
            }
          }
          return false;
        }

        setActive(index) {
          this.index = index;
          this.dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
          });
        }
      }
    );
  }

  /* ---------------- Swatches ---------------- */
  function setText(el, text) {
    if (el) el.textContent = text;
  }

  function updatePrice(card, data) {
    var price = card.querySelector('.card__content .price');
    if (!price) return;
    var onSale = data.onSale === 'true' && data.compare;
    price.classList.toggle('price--on-sale', !!onSale);
    price.classList.toggle('price--sold-out', data.available !== 'true');
    price.classList.remove('price--no-compare');
    setText(price.querySelector('.price__regular .price-item--regular'), data.price);
    setText(price.querySelector('.price__sale .price-item--regular'), data.compare || '');
    setText(price.querySelector('.price__sale .price-item--sale'), data.price);
  }

  function updateLinks(card, url) {
    if (!url) return;
    card.querySelectorAll('a.full-unstyled-link, a.card-carousel__link').forEach(function (a) {
      a.setAttribute('href', url);
    });
    var modalBtn = card.querySelector('.quick-add [data-product-url]');
    if (modalBtn) modalBtn.setAttribute('data-product-url', url);
    var chooseLink = card.querySelector('.quick-add a.quick-add__submit');
    if (chooseLink) chooseLink.setAttribute('href', url);
  }

  function updateQuickAdd(card, data) {
    var form = card.querySelector('.quick-add product-form form');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = data.variantId;
    var btn = form.querySelector('button[type="submit"]');
    if (!btn) return;
    var available = data.available === 'true';
    var label = btn.querySelector('span:not(.sold-out-message):not(.icon-wrap):not(.loading__spinner)');
    var strings = window.variantStrings || {};
    if (available) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      setText(label, strings.addToCart || 'Add to cart');
    } else {
      btn.setAttribute('disabled', '');
      setText(label, strings.soldOut || 'Sold out');
    }
  }

  function updateImage(card, data) {
    var carousel = card.querySelector('card-carousel');
    if (!carousel) return;
    // Preferred: switch to this variant's image group (its cover image + the images that follow it).
    if (typeof carousel.setGroup === 'function' && carousel.setGroup(data.variantId)) return;
    // Fallback: no group for this variant, just jump to its cover image.
    if (typeof carousel.goToMedia === 'function' && carousel.goToMedia(data.mediaId)) return;
    // Variant image isn't one of the card's slides: swap the first slide's image instead.
    if (data.image) {
      var firstImg = carousel.querySelector('.card-carousel__slide img');
      if (firstImg) {
        firstImg.removeAttribute('srcset');
        firstImg.src = data.image;
        if (typeof carousel.goTo === 'function') carousel.goTo(0);
      }
    }
  }

  function onSwatchClick(event) {
    var swatch = event.target.closest('.card-swatch');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();

    var card = swatch.closest('.card-wrapper');
    if (!card) return;
    var data = swatch.dataset;

    card.querySelectorAll('.card-swatch').forEach(function (s) {
      var active = s === swatch;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    updateImage(card, data);
    updatePrice(card, data);
    updateLinks(card, data.url);
    updateQuickAdd(card, data);
  }

  function onSwatchKeydown(event) {
    var swatch = event.target.closest('.card-swatch');
    if (!swatch) return;
    var group = swatch.closest('.card-swatches');
    if (!group) return;
    var swatches = Array.prototype.slice.call(group.querySelectorAll('.card-swatch'));
    var i = swatches.indexOf(swatch);
    var next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = swatches[(i + 1) % swatches.length];
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = swatches[(i - 1 + swatches.length) % swatches.length];
    if (next) {
      event.preventDefault();
      next.focus();
      next.click();
    }
  }

  document.addEventListener('click', onSwatchClick);
  document.addEventListener('keydown', onSwatchKeydown);
})();
